import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Popconfirm,
  Tag,
  Space,
  Input,
  Typography,
  Descriptions,
  Divider,
  Collapse,
  Image,
  message,
} from 'antd';
import { useOrder } from '../../store/orderContext';
import { Order } from '../../types/order';
import { updateOrderStatus } from '../../api/orderApi';
import { useTranslation } from 'react-i18next';
import PrintInvoiceButton from '../Invoice/PrintInvoiceButton';
import PageNavigation from '../Layout/PageNavigation';
import { useAuth } from '../../store/AuthContext';

const { Link, Text } = Typography;
const { Panel } = Collapse;

interface Props {
  onEdit: (order: Order) => void;
}

const OrderList: React.FC<Props> = ({ onEdit }) => {
  const { orders, deleteOrder, fetchOrdersByStatus, downloadOrdersPdf } = useOrder();
  const { t, i18n } = useTranslation();
  const [search, setSearch] = useState('');
  const { hasPermission } = useAuth();

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedOrders, setSelectedOrders] = useState<Order[]>([]);
  const [pdfLoading, setPdfLoading] = useState(false);

  const statusFlow = ['processing', 'ordered', 'shipped', 'inStore', 'delivered'];

  const getNextStatus = (current: string) => {
    const index = statusFlow.indexOf(current);
    return index < statusFlow.length - 1 ? statusFlow[index + 1] : null;
  };

  const getPreviousStatus = (current: string) => {
    const index = statusFlow.indexOf(current);
    return index > 0 ? statusFlow[index - 1] : null;
  };

  const handleChangeStatus = async (order: Order) => {
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) return message.info(t('already_last_status'));
    try {
      await updateOrderStatus(order.id.toString(), nextStatus);
      message.success(t('status_updated', { status: t(nextStatus) }));
      fetchOrdersByStatus(order.status);
    } catch (error) {
      message.error(t('status_update_failed'));
    }
  };

  const handleBackStatus = async (order: Order) => {
    const previousStatus = getPreviousStatus(order.status);
    if (!previousStatus) return message.info(t('already_first_status'));
    try {
      await updateOrderStatus(order.id.toString(), previousStatus);
      message.success(t('status_reverted', { status: t(previousStatus) }));
      fetchOrdersByStatus(order.status);
    } catch (error) {
      message.error(t('status_revert_failed'));
    }
  };
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const downloadImage = (url: string, filename?: string) => {
    if (isMobile) {
      window.open(url, '_blank'); // fallback for mobile
      return;
    }
    let ext = url.split('.').pop()?.split('?')[0] || 'jpg';
    let defaultName = 'image.' + ext;
    fetch(url)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || defaultName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(blobUrl);
        a.remove();
      })
      .catch(() => {
        message.error('Failed to download image');
      });
  };
  const filteredOrders = useMemo(() => {
    return orders.filter(order =>
      (order.customerCode ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (order.status ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (order.cardLink ?? '').toLowerCase().includes(search.toLowerCase()) ||
      (order.totalPrice ?? '').toString().includes(search) ||
      (order.pay ?? '').toString().includes(search) ||
      (order.remain ?? '').toString().includes(search)
    );
  }, [orders, search]);

  const expandedRowRender = (order: Order) => (
    <>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label={t('customerCode')}>{order.customerCode}</Descriptions.Item>
        <Descriptions.Item label={t('card_link')}>
          <Link href={order.cardLink} target="_blank">{order.cardLink}</Link>
        </Descriptions.Item>
        <Descriptions.Item label={t('total_price')}>{order.totalPrice}</Descriptions.Item>
        <Descriptions.Item label={t('pay')}>{order.pay}</Descriptions.Item>
        <Descriptions.Item label={t('remain')}>{order.remain}</Descriptions.Item>
        <Descriptions.Item label={t('status')}>
          <Tag color="blue">{t(order.status)}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label={t('created_at')}>
          {order.createdAt ? new Date(order.createdAt).toLocaleString() : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('updated_at')}>
          {order.updatedAt ? new Date(order.updatedAt).toLocaleString() : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('delivery_cost')}>
          {order.deliveryCost !== undefined ? order.deliveryCost : '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('invoice_number')}>
          {order.invoiceNumber ?? '—'}
        </Descriptions.Item>
        <Descriptions.Item label={t('qty')}>
          {order.qty ?? '—'}
        </Descriptions.Item>
      </Descriptions>
      <Divider orientation="left" style={{ marginTop: 24 }}>{t('order_items')}</Divider>
      <Collapse ghost>
        {order.items?.map((item, index) => (
          <Panel header={`${t('item')} ${index + 1}`} key={index}>
            <Space direction="vertical" size="small">
              {item.image && (
                <>
                  <Image width={100} src={item.image} preview={{ src: item.image }} />
                  <Button
                    size="small"
                    style={{ marginTop: 8 }}
                    onClick={() => downloadImage(item.image, `order_item_${index + 1}.jpg`)}
                  >
                    {t('download_image') || 'Download Image'}
                  </Button>
                </>
              )}
              <Text><strong>{t('quantity')}:</strong> {item.qty}</Text>
              <Text><strong>{t('note')}:</strong> {item.note ?? '—'}</Text>
              <Text><strong>{t('detail')}:</strong> {item?.note ?? '—'}</Text>
              <Text><strong>{t('customization_note')}:</strong> {item.customization?.note ?? '—'}</Text>
              <Text><strong>{t('customization_detail')}:</strong> {item.customization?.detail ?? '—'}</Text>
            </Space>
          </Panel>
        ))}
      </Collapse>
      <Divider orientation="left" style={{ marginTop: 24 }}>{t('sub_orders')}</Divider>
      <Collapse ghost>
        {order.subOrders?.map((sub, index) => (
          <Panel header={`${t('sub_order')} ${index + 1}`} key={`sub-${index}`}>
            <Text><strong>{t('link')}:</strong> <Link href={sub.link} target="_blank">{sub.link}</Link></Text><br />
            <Text><strong>{t('pieces')}:</strong> {sub.pieces}</Text><br />
            <Text><strong>{t('note')}:</strong> {sub.note ?? '—'}</Text>
          </Panel>
        ))}
      </Collapse>
      <Divider orientation="left" style={{ marginTop: 24 }}>{t('order_details')}</Divider>
      <Collapse ghost>
        {order.orderDetails?.map((detail, index) => (
          <Panel header={`${t('order_detail')} ${index + 1}`} key={`detail-${index}`}>
            <Text><strong>{t('order_no')}:</strong> {detail.orderNo}</Text><br />
            <Text><strong>{t('tracking_no')}:</strong> {detail.orderTrackNo}</Text><br />
            <Text><strong>{t('account_no')}:</strong> {detail.accountNo ?? '—'}</Text>
          </Panel>
        ))}
      </Collapse>
    </>
  );

  const columns = [
    {
      title: t('customer'),
      dataIndex: 'customerCode',
    },
    {
      title: t('card_link'),
      dataIndex: 'cardLink',
      render: (link: string) => <Link href={link} target="_blank">{t('open_link')}</Link>,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      render: (status: string) => <Tag color="blue">{t(status)}</Tag>,
    },
    {
      title: t('actions'),
      render: (_: any, record: Order) => (
        <div
          style={{
            display: 'flex',
            justifyContent: i18n.language === 'ar' || i18n.language === 'ckb' ? 'flex-end' : 'flex-start',
          }}
        >
          <Space>
            {hasPermission('order.update') && (
              <Button type="primary" size="small" onClick={() => onEdit(record)}>
                {t('edit')}
              </Button>
            )}
            {hasPermission('order.nextStatus') ? (
              <Button size="small" onClick={() => handleChangeStatus(record)}>
                ➡ {t('next')}
              </Button>
            ) : null}
            {hasPermission('order.prevStatus') ? (
              <Button size="small" danger onClick={() => handleBackStatus(record)}>
                ⬅ {t('back')}
              </Button>
            ) : null}
            {hasPermission('order.delete') ? (
              <Popconfirm title={t('sure_to_delete')} onConfirm={() => deleteOrder(record.id)}>
                <Button size="small" type="primary" danger>
                  {t('delete')}
                </Button>
              </Popconfirm>
            ) : null}
            <PrintInvoiceButton order={record} />
          </Space>
        </div>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[], selectedRows: Order[]) => {
      setSelectedRowKeys(selectedKeys);
      setSelectedOrders(selectedRows);
    },
  };

  // === PDF Download Button ===
  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const blob = await downloadOrdersPdf(selectedOrders.map(o => o.id), i18n.language);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'orders.pdf';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }, 0);
      message.success(t('download_success') || 'PDF downloaded!');
    } catch (e) {
      message.error(t('download_failed') || 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div
      style={{
        overflowX: 'auto',
        direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr',
      }}
    >
      <PageNavigation title={t('orders') as string} />
      <Button
        type="primary"
        loading={pdfLoading}
        disabled={selectedOrders.length === 0}
        style={{ marginBottom: 16, marginRight: 12 }}
        onClick={handleDownloadPdf}
      >
        {t('download_pdf') || 'Download PDF'}
      </Button>
      <Input.Search
        allowClear
        style={{ marginBottom: 16, maxWidth: 350 }}
        placeholder={t('search_orders') as string}
        onChange={(e) => setSearch(e.target.value)}
        value={search}
      />
      <Table
        rowKey="id"
        dataSource={filteredOrders}
        columns={columns}
        rowSelection={rowSelection}
        scroll={{ x: 'max-content' }}
        expandable={{ expandedRowRender }}
        pagination={{ pageSize: 6 }}
        size="middle"
      />
    </div>
  );
};

export default OrderList;
